/**
 * Modal de **cambio de contraseña** (`POST /users/<username>/password`).
 *
 * Réplica de `change_password.html` del SSR (`progress/explore_equipo_ssr.md`,
 * §B.4): contraseña (6+ caracteres) + confirmación que coincide. Valida con Zod
 * (`changePasswordSchema`) antes de delegar; el error de la API se traduce a
 * mensaje legible. Controlado por `username` del padre (`null` = cerrado).
 */
import { useEffect, useState } from "react";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
} from "@/components/ui";

import { teamErrorMessage } from "../format";
import { useChangePassword } from "../hooks/useUserMutations";
import { changePasswordSchema } from "../schemas";

interface ChangePasswordDialogProps {
  /** Usuario al que se le cambia la contraseña; `null` mantiene el modal cerrado. */
  username: string | null;
  onClose: () => void;
}

type FieldErrors = Partial<Record<"password" | "confirm_password", string>>;

export function ChangePasswordDialog({
  username,
  onClose,
}: ChangePasswordDialogProps) {
  const changePassword = useChangePassword();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  useEffect(() => {
    if (username !== null) {
      setPassword("");
      setConfirm("");
      setFieldErrors({});
      changePassword.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (username === null) {
      return;
    }
    const result = changePasswordSchema.safeParse({
      password,
      confirm_password: confirm,
    });
    if (!result.success) {
      const flattened = result.error.flatten().fieldErrors;
      const errors: FieldErrors = {};
      const passwordError = flattened.password?.[0];
      const confirmError = flattened.confirm_password?.[0];
      if (passwordError) {
        errors.password = passwordError;
      }
      if (confirmError) {
        errors.confirm_password = confirmError;
      }
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    changePassword.mutate(
      { username, body: { password: result.data.password } },
      { onSuccess: onClose },
    );
  }

  const apiError = changePassword.isError
    ? teamErrorMessage(changePassword.error)
    : null;

  return (
    <Dialog
      open={username !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogTitle>Cambiar contraseña</DialogTitle>
        <DialogDescription>Usuario: {username ?? ""}</DialogDescription>

        <form className="mt-4 flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-1">
            <label htmlFor="pwd-new" className="text-sm font-medium text-slate-700">
              Nueva contraseña
            </label>
            <Input
              id="pwd-new"
              type="password"
              autoComplete="new-password"
              value={password}
              aria-invalid={fieldErrors.password ? true : undefined}
              aria-describedby={fieldErrors.password ? "pwd-new-error" : undefined}
              onChange={(event) => setPassword(event.target.value)}
            />
            {fieldErrors.password && (
              <p id="pwd-new-error" className="text-sm text-red-600">
                {fieldErrors.password}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="pwd-confirm" className="text-sm font-medium text-slate-700">
              Confirmar contraseña
            </label>
            <Input
              id="pwd-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              aria-invalid={fieldErrors.confirm_password ? true : undefined}
              aria-describedby={
                fieldErrors.confirm_password ? "pwd-confirm-error" : undefined
              }
              onChange={(event) => setConfirm(event.target.value)}
            />
            {fieldErrors.confirm_password && (
              <p id="pwd-confirm-error" className="text-sm text-red-600">
                {fieldErrors.confirm_password}
              </p>
            )}
          </div>

          {apiError && (
            <p role="alert" className="text-sm text-red-600">
              {apiError}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={changePassword.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={changePassword.isPending}>
              {changePassword.isPending ? "Guardando…" : "Cambiar contraseña"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
